import type { ReactNode } from 'react';
import { ProfileHero, type ProfileHeroStat } from '@/components/common/ProfileHero';
import { ProfileLogo } from '@/components/common/ProfileLogo';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';
import {
  formatCompactCurrency,
  formatCompactCurrencyRange,
  humanizeToken,
  displayUrl,
} from '@/lib/utils/format.utils';
import type { StartupProfileData } from '../../api/startup-profile.api';
import { STARTUP_PROFILE_STRINGS } from './startup-profile.strings';
import { StartupCompletionMenu } from './StartupCompletionMenu';
import type { StartupSectionId } from './startup-sections';

const S = UI_STRINGS.STARTUP_PROFILE;

export interface StartupProfileHeroProps {
  data: StartupProfileData;
  /** True hides the completion % and its menu (FR-169). */
  readOnly: boolean;
  canExport: boolean;
  isExporting: boolean;
  onExport: () => void;
  onOpenSection: (section: StartupSectionId) => void;
  /** Pills beside the name, e.g. the shared page's "Read only" (FR-180). */
  badges?: ReactNode;
  /** Where "Preview as investor" goes; omitted where the caller cannot preview. */
  previewTo?: string;
}

function startupMeta(data: StartupProfileData) {
  const primarySector = data.sectors?.find((row) => row.isPrimary)?.sector;
  const hq = [data.hqCity, data.hqState].filter(Boolean).join(', ');
  return (
    <>
      {primarySector && <span>{primarySector}</span>}
      {data.fundingStage && <span>{humanizeToken(data.fundingStage)}</span>}
      {data.yearFounded && <span>{STARTUP_PROFILE_STRINGS.FOUNDED(data.yearFounded)}</span>}
      {hq && <span>{hq}</span>}
      {data.companyWebsiteUrl && (
        <a
          href={data.companyWebsiteUrl}
          target="_blank"
          rel="noreferrer"
          className="text-green hover:underline"
        >
          {displayUrl(data.companyWebsiteUrl)}
        </a>
      )}
      {data.companyLinkedinUrl && (
        <a
          href={data.companyLinkedinUrl}
          target="_blank"
          rel="noreferrer"
          className="text-green hover:underline"
        >
          {STARTUP_PROFILE_STRINGS.LINKEDIN}
        </a>
      )}
    </>
  );
}

/**
 * The B header strip: Raising, ARR, Raised to date, Team, Profile complete.
 * Every column always shows, with a dash when its value is empty (client
 * feedback 2026-10-15), so Profile complete stays on the right.
 */
function startupStats(
  data: StartupProfileData,
  completion: ProfileHeroStat['value'],
): ProfileHeroStat[] {
  const T = STARTUP_PROFILE_STRINGS;
  const stats: ProfileHeroStat[] = [
    {
      label: T.RAISING,
      value: formatCompactCurrencyRange(data.financeSoughtLow, data.financeSoughtHigh),
    },
    { label: T.ARR, value: formatCompactCurrency(data.arr) },
    { label: T.RAISED_TO_DATE, value: formatCompactCurrency(data.amountRaisedToDate) },
    {
      label: T.TEAM,
      value: data.employeeCount != null ? T.TEAM_SIZE(data.employeeCount) : undefined,
    },
  ];
  return completion ? [...stats, { label: L.PROFILE_COMPLETE, value: completion }] : stats;
}

/** Startup profile header (FR-170): identity, Export as PDF, and the key-number strip. */
export function StartupProfileHero({
  data,
  readOnly,
  canExport,
  isExporting,
  onExport,
  onOpenSection,
  badges,
  previewTo,
}: StartupProfileHeroProps) {
  const completion =
    !readOnly && data.completion ? (
      <StartupCompletionMenu completion={data.completion} onOpenSection={onOpenSection} />
    ) : undefined;
  const actions =
    canExport || previewTo ? (
      <div className="flex flex-wrap gap-3">
        {previewTo && (
          <Button asChild variant="outline-light" className="inline-flex">
            <Link to={previewTo}>{S.PREVIEW_AS_INVESTOR}</Link>
          </Button>
        )}
        {canExport && (
          <Button variant="outline-light" onClick={onExport} disabled={isExporting}>
            {isExporting ? S.EXPORT_PDF_GENERATING : S.EXPORT_PDF}
          </Button>
        )}
      </div>
    ) : undefined;

  return (
    <ProfileHero
      avatar={<ProfileLogo url={data.logoUrl} name={data.companyName} ring="dark" />}
      name={data.companyName}
      badges={badges}
      description={data.briefDescription ?? undefined}
      meta={startupMeta(data)}
      actions={actions}
      stats={startupStats(data, completion)}
    />
  );
}
